import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button } from '../atoms';

/**
 * EmptyState Molecule
 * A component to display when there's no content to show
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Main message
 * @param {string} props.description - Additional description
 * @param {React.ReactNode} props.icon - Icon to display
 * @param {string} props.actionText - Text for the action button
 * @param {Function} props.onAction - Function to call when action button is clicked
 * @param {Object} props.sx - Additional styles
 * @returns {React.ReactElement} EmptyState component
 */
const EmptyState = ({
  title = 'No items found',
  description = 'There are no items to display at this time.',
  icon,
  actionText,
  onAction,
  sx = {},
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 6,
        px: 3,
        borderRadius: 2,
        backgroundColor: (theme) => theme.palette.background.default,
        height: '100%',
        minHeight: 200,
        ...sx
      }}
    >
      {/* Icon */}
      {icon && (
        <Box sx={{ mb: 2, color: 'text.secondary' }}>
          {icon}
        </Box>
      )}
      
      {/* Title */}
      <Typography variant="h6" component="h3" gutterBottom>
        {title}
      </Typography>
      
      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mb: 3 }}>
        {description}
      </Typography>
      
      {/* Action Button */}
      {actionText && onAction && (
        <Button
          variant="contained"
          color="primary"
          onClick={onAction}
        >
          {actionText}
        </Button>
      )}
    </Box>
  );
};

EmptyState.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  icon: PropTypes.node,
  actionText: PropTypes.string,
  onAction: PropTypes.func,
  sx: PropTypes.object
};

export default EmptyState;